import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Check, CreditCard, Zap } from "lucide-react";

const subscriptionTiers = [
  {
    name: "Basic",
    price: "$29",
    period: "month",
    description: "Perfect for new brokers",
    features: [
      "Up to 5 active listings",
      "Basic analytics",
      "Email support",
      "Standard lead management"
    ],
    priceId: "price_1RxbNKG4Mrq3Df99ErpsTnY2",
    productId: "prod_StO98hJBSsDED5",
    popular: false
  },
  {
    name: "Broker Package", 
    price: "$99",
    period: "month",
    description: "Exclusively for licensed business brokers (minimum 5 listings required)",
    features: [
      "Minimum 5 active listings required",
      "Unlimited listings",
      "AI-powered business matching",
      "Advanced analytics & reports", 
      "Priority support",
      "Automated valuation tools",
      "Custom branding"
    ],
    priceId: "price_1S7UVFG4Mrq3Df99nwGrDYwE",
    productId: "prod_T3biLDoNAQDNki",
    popular: true
  },
  {
    name: "Enterprise",
    price: "$299", 
    period: "month",
    description: "For large brokerage firms",
    features: [
      "Everything in Professional",
      "White-label solution",
      "API access",
      "Dedicated account manager",
      "Custom integrations",
      "Advanced compliance tools"
    ],
    priceId: "price_1S7UVaG4Mrq3Df99ENWMLbDp",
    productId: "prod_T3bjuiCi8B2IQO",
    popular: false
  }
];

export function BrokerBilling() {
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
      setCurrentSubscription(data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId }
      });
      
      if (error) throw error;
      
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Failed to start checkout process",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error", 
        description: "Failed to open subscription management",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Billing & Settings</h1>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Billing & Settings</h1>
        <p className="text-muted-foreground">Manage your subscription and billing information</p>
      </div>

      {currentSubscription?.subscribed && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">
                  {subscriptionTiers.find(tier => tier.productId === currentSubscription.product_id)?.name || 'Active Subscription'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {currentSubscription.subscription_end && `Renews on ${new Date(currentSubscription.subscription_end).toLocaleDateString()}`}
                </p>
              </div>
              <Button onClick={handleManageSubscription} variant="outline">
                Manage Subscription
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-2xl font-semibold mb-6">Choose Your Plan</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {subscriptionTiers.map((tier) => (
            <Card key={tier.name} className={`relative ${tier.popular ? 'border-primary shadow-lg' : ''}`}>
              {tier.popular && (
                <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary">
                  Most Popular
                </Badge>
              )}
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  {tier.name}
                  {currentSubscription?.product_id === tier.productId && (
                    <Badge variant="secondary">Current</Badge>
                  )}
                </CardTitle>
                <CardDescription>{tier.description}</CardDescription>
                <div className="text-3xl font-bold">
                  {tier.price}
                  <span className="text-base font-normal text-muted-foreground">/{tier.period}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full" 
                  variant={tier.popular ? "default" : "outline"}
                  onClick={() => handleSubscribe(tier.priceId)}
                  disabled={currentSubscription?.product_id === tier.productId}
                >
                  {currentSubscription?.product_id === tier.productId ? 'Current Plan' : 'Subscribe'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Subscription Benefits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="font-semibold mb-2">AI-Powered Matching</h4>
              <p className="text-sm text-muted-foreground">
                Get intelligent buyer-business matches based on advanced algorithms and preferences.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Automated Valuations</h4>
              <p className="text-sm text-muted-foreground">
                Receive AI-generated business valuations to help price your listings competitively.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Advanced Analytics</h4>
              <p className="text-sm text-muted-foreground">
                Track performance metrics, lead conversion rates, and market insights.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Priority Support</h4>
              <p className="text-sm text-muted-foreground">
                Get faster response times and dedicated support for your business needs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}